import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import colors from 'utils/colors';
import styled from 'styled-components';
import {
  Container,
  HeadingTitle,
  PaddingWrapper,
  HeaderFlexRow,
  PlainButton,
} from 'components/styled-components';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import Contacts from 'components/vendors/contacts';
import Renewals from 'components/vendors/renewals';
import Requests from 'components/vendors/requests';
import {
  fetchVendorContacts,
  fetchVendorRequestsByUserType,
} from 'actions/vendor-request';
import { fetchAllVendorsManagement, setVendor } from 'actions/vendors';
import { fetchAllVendorStatus } from 'actions/utils';
import {
  Button,
  InputGroup,
  Switch,
  Intent,
  NonIdealState,
} from '@blueprintjs/core';
import { updateVendorDetails, createVendor } from 'api/vendor-request';
import moment from 'moment';
import { AppToaster } from 'components/AppToaster';
import hasPermission from 'utils/hasPermission';
import permissions from 'utils/permissions';

class ManageVendorDetails extends Component {
  state = {
    newVendor: false,
    editing: false,
    requests: [],
    renewalDate: new Date(),
    vendorName: '',
    certificate: false,
    vendorRequestID: '',
    productServiceName: '',
    editMade: false,
    originalVendor: {},
    errorFetchingVendor: false,
    create: false,
    status: '',
    mergeDate: null,
    mergeTargetName: '',
  };

  componentDidMount() {
    const { vendorID } = this.props.match.params;
    const { vendor, loaded } = this.props;
    this.props.fetchAllVendorStatus();
    // if url is /manage-parties/new, set the state so it is ready to create and already editing
    if (vendorID === 'new') {
      this.setState({ create: true, editing: true });
    } else if (!isNaN(vendorID)) {
      // ensure the vendorID passed is an integer and check if we have a vendor in the store

      // if there is a vendor in the redux store, we navigated here from the list of vendors page
      if (vendor) {
        // spread over the vendor object to easily edit the values and set the originalVendor to allow for cancel / reset easily
        this.setState({ ...cloneDeep(vendor), originalVendor: vendor });
        // fetch the contacts for the provided vendor
        this.props.fetchVendorContacts(vendor.vendorName);
      } else if (loaded) {
        // check if the store is populated with management values already
        this.findVendorFromManagement(vendorID);
      } else {
        // have to fetch from the management end point to get all of the proper vendor data
        this.props.fetchAllVendorsManagement({ start: 0, limit: -1 });
      }

      // fetch the vendor's requests by ID
      this.props.fetchVendorRequestsByUserType({
        vendorID,
        start: 0,
        limit: -1,
      });
    } else {
      // set the error if the vendorID isn't 'new' or an integer
      this.setState({ errorFetchingVendor: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { vendorID } = this.props.match.params;
    const { management, loaded, requests } = this.props;
    if (vendorID !== 'new' && !isNaN(vendorID)) {
      // if new data from the management end point has loaded in, find the vendor from the list
      if (loaded && !isEqual(prevProps.management, management)) {
        this.findVendorFromManagement(vendorID);
      }
      if (!isEqual(prevState.requests.length, requests.length)) {
        this.setState({ requests: cloneDeep(requests) });
      }
    }
  }

  componentWillUnmount() {
    // clear the vendor from the redux store when leaving this page
    this.props.setVendor(null);
  }

  findVendorFromManagement = vendorID => {
    // this function finds the vendor based on ID from the management response
    // and spreads the data into the state object and sets the originalVendor value
    const { management } = this.props;
    const vendor = find(
      management.result,
      ven => ven.vendorID === parseInt(vendorID)
    );
    if (vendor) {
      this.setState({ ...vendor, originalVendor: vendor });
      this.props.fetchVendorContacts(vendor.vendorName);
    } else {
      this.setState({ errorFetchingVendor: true });
    }
  };

  handleTextChange = e => {
    const vendorName = e.target.value;
    this.setState({ vendorName });
    if (!this.state.editMade) {
      this.toggleEditMade(true);
    }
  };

  toggleEdit = editing => this.setState({ editing });

  toggleEditMade = editMade => this.setState({ editMade });

  handleDateChange = renewalDate => {
    this.setState({ renewalDate });
    this.toggleEditMade(true);
  };

  handleCertificateChange = val => {
    this.setState({ certificate: !val });
    this.toggleEditMade(true);
  };

  handleStatusClick = () => {
    this.state.status === 'Active'
      ? this.setState({ status: 'Inactive' })
      : this.setState({ status: 'Active' });
    this.toggleEditMade(true);
  };

  handleCancelClick = () => {
    const { vendorID } = this.props.match.params;
    this.resetRequestsAndVendor();
    this.setState({ editing: false });
    if (vendorID === 'new') {
      this.props.history.push(`/manage-parties`);
      return;
    }
  };

  resetRequestsAndVendor = () => {
    this.setState({
      ...cloneDeep(this.state.originalVendor),
      requests: cloneDeep(this.props.requests),
    });
  };

  createOrUpdate = () => {
    const { vendorID } = this.props.match.params;
    vendorID === 'new' ? this.submit() : this.handleSave();
  };

  submit = () => {
    const { vendorName, certificate } = this.state;
    createVendor({ vendorName, certificate })
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.props.history.push(`/manage-parties`);
      })
      .catch(err =>
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        })
      );
  };

  handleSave = () => {
    const {
      vendorName,
      certificate,
      vendorID,
      vendorRequestID,
      renewalDate,
    } = this.state;

    let requestBody = {
      vendorName,
      certificate: certificate ? true : false,
    };
    if (vendorRequestID && moment(renewalDate).isValid()) {
      requestBody = {
        ...requestBody,
        vendorRequestID,
        renewalDate: moment(renewalDate).format('YYYY-MM-DD'),
      };
    }

    updateVendorDetails(vendorID, requestBody)
      .then(res =>
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        })
      )
      .catch(err =>
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        })
      );
    this.props.fetchAllVendorsManagement({ start: 0, limit: -1 });
    this.props.fetchVendorRequestsByUserType({
      vendorID,
      start: 0,
      limit: -1,
    });
    this.toggleEdit(false);
  };

  render() {
    const {
      vendorName,
      certificate,
      editing,
      editMade,
      vendorID,
      errorFetchingVendor,
      create,
      status,
      mergeDate,
      mergeTargetName,
    } = this.state;

    const { appUser } = this.props;
    const showEditButtons = status.toUpperCase() !== 'MERGED';

    if (errorFetchingVendor) {
      return (
        <Container width="50%">
          <PaddingWrapper>
            <NonIdealState
              icon="search"
              title="Unable to Find Third Party"
              description="The Third Party you are searching for could not be found."
              action={
                <Link to="/manage-parties">Back to Third Party Management</Link>
              }
            />
          </PaddingWrapper>
        </Container>
      );
    }
    return (
      <div>
        {(this.props.loaded || create) && (
          <Container>
            <HeaderFlexRow>
              <HeadingTitle>Third Party Profile</HeadingTitle>
              {/* if editing is true show cancel and save buttons */}
              {editing && (
                <div>
                  <Button
                    style={{ marginRight: '1rem' }}
                    minimal
                    onClick={this.handleCancelClick}
                  >
                    Cancel
                  </Button>

                  <PlainButton
                    onClick={() => this.createOrUpdate()}
                    intent="primary"
                    disabled={!editMade}
                  >
                    SAVE
                  </PlainButton>
                </div>
              )}
              {hasPermission(
                appUser.permissions,
                permissions.MODIFY_THIRD_PARTY
              ) &&
                showEditButtons &&
                !editing && (
                  <PlainButton
                    onClick={() => this.toggleEdit(true)}
                    intent="primary"
                  >
                    Edit
                  </PlainButton>
                )}
            </HeaderFlexRow>
            <BlueSection>
              <NameWrapper>
                <span>Third Party Name</span>
                {/* if vendorID exists and editing is true show input field */}
                {vendorID && editing && (
                  <H1>
                    <InputGroup
                      large
                      value={vendorName}
                      className="medium-text-group"
                      name="vendorName"
                      onChange={this.handleTextChange}
                      maxLength={100}
                    />
                  </H1>
                )}
                {/* if vendor name is true and editing is false show vendor name */}
                {vendorName && !editing ? <H1>{vendorName}</H1> : null}
                {/* if vendorID is false and create is true show input field */}
                {!vendorID && create && (
                  <H1>
                    <InputGroup
                      large
                      placeholder="Add Name"
                      className="medium-text-group"
                      name="vendorName"
                      onChange={this.handleTextChange}
                      maxLength={100}
                    />
                  </H1>
                )}
              </NameWrapper>
            </BlueSection>
            <PaddingWrapper>
              <Section>
                <Column>
                  {vendorID && (
                    <Contacts
                      data={this.props.contacts}
                      vendorID={vendorID}
                      vendorName={vendorName}
                      fetchVendorContacts={this.props.fetchVendorContacts}
                      appUser={this.props.appUser}
                      showAddButton={showEditButtons}
                    />
                  )}
                  <CertificateWrapper>
                    <Certificate>
                      <span>Certificate on File?</span>
                      <Switch
                        innerLabel="No"
                        innerLabelChecked="Yes"
                        large
                        disabled={!editing}
                        checked={certificate}
                        onChange={() =>
                          this.handleCertificateChange(certificate)
                        }
                      />
                    </Certificate>
                  </CertificateWrapper>
                  {vendorID && (
                    <CertificateWrapper>
                      <Certificate>
                        <span>
                          Status: <strong>{status}</strong>
                        </span>
                      </Certificate>
                    </CertificateWrapper>
                  )}
                  {status === 'Merged' && (
                    <CertificateWrapper width="100%">
                      <CertificateColumn>
                        <span>Date Merged:</span>
                        <span style={{ paddingTop: '5px' }}>
                          Merged into Third Party with ID:
                        </span>
                      </CertificateColumn>
                      <CertificateColumn>
                        <div>
                          <strong>{mergeDate}</strong>
                        </div>
                        <div style={{ paddingTop: '5px' }}>
                          <strong>{mergeTargetName}</strong>
                        </div>
                      </CertificateColumn>
                    </CertificateWrapper>
                  )}
                </Column>

                {vendorID && (
                  <Renewals
                    renewalDate={this.state.renewalDate}
                    productServiceName={this.state.productServiceName}
                    editing={editing}
                    handleDateChange={this.handleDateChange}
                  />
                )}
              </Section>
              {vendorID && (
                <Requests
                  requests={this.props.requests}
                  loading={this.props.loading}
                />
              )}
            </PaddingWrapper>
          </Container>
        )}
      </div>
    );
  }
}

const Section = styled.section`
  margin-top: -4rem;
  display: flex;
  align-content: center;
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-right: 2rem;
`;

const CertificateWrapper = styled.div`
  margin: 1rem;
  border: 2pt solid ${colors.brandingPrimary};
  border-radius: 50px;
  width: ${props => props.width || '50%'};
  padding: 1rem;
  display: flex;
  justify-content: center;
  background: #fff;
  min-width: 18rem;
`;

const Certificate = styled.div`
  display: flex;
  align-items: center;

  & span {
    font-size: 1rem;
    margin-right: 1rem;
    color: ${colors.darkGray};
  }

  & label {
    margin-top: 0.7rem;
  }
`;

const CertificateColumn = styled.div`
  display: flex;
  flex-direction: column;

  & span {
    display: flex;
    justify-content: right;
    font-size: 1rem;
    margin-right: 1rem;
    color: ${colors.darkGray};
  }

  & div {
    display: flex;
    justify-content: center;
    font-size: 1rem;
    margin-right: 1rem;
    color: ${colors.darkGray};
  }
`;

const BlueSection = styled.div`
  padding: 2rem;
  padding-bottom: 4rem;
  color: #fff;
  background-color: ${colors.brandingPrimary};
  display: flex;
  justify-content: space-between;
`;

const NameWrapper = styled.div`
  flex-grow: 1;
`;

const H1 = styled.h1`
  font-weight: 400;
`;

const mapStateToProps = state => ({
  vendor: state.vendors.editing,
  fetchedVendor: state.vendors.single,
  contacts: state.vendorRequest.contacts,
  management: state.vendors.management,
  isFetching: state.vendors.isFetchingManagement,
  loaded: state.vendors.managementLoaded,
  requests: state.vendorRequest.allRequestsByUserType,
  loading: state.vendorRequest.isFetching,
  vendorStatus: state.utils.vendorStatus,
});

ManageVendorDetails = withRouter(
  connect(
    mapStateToProps,
    {
      fetchAllVendorsManagement,
      fetchVendorContacts,
      fetchVendorRequestsByUserType,
      setVendor,
      fetchAllVendorStatus,
    }
  )(ManageVendorDetails)
);

export default ManageVendorDetails;
